import { useEffect, useRef, useState } from 'react';
import StudioCardImage from './StudioCardImage';
import { GenderCardMedia, GenderCardVideo } from '../studio.styles';

const playVideoSafely = (video) => {
	if (!video) {
		return;
	}

	video.play().catch(() => {});
};

const StudioCardHoverMedia = ({
	imageSrc,
	videoSrc,
	videoFallback = '',
	alt,
	priority = false,
	objectPosition = 'center top',
	isActive = false,
}) => {
	const videoRef = useRef(null);
	const [resolvedVideoSrc, setResolvedVideoSrc] = useState(videoSrc);
	const [isVideoReady, setIsVideoReady] = useState(false);
	const [hasVideoError, setHasVideoError] = useState(false);
	const hasVideo = Boolean(resolvedVideoSrc) && !hasVideoError;
	const showVideo = hasVideo && isVideoReady && isActive;

	useEffect(() => {
		setResolvedVideoSrc(videoSrc);
		setIsVideoReady(false);
		setHasVideoError(false);
	}, [videoSrc]);

	useEffect(() => {
		const video = videoRef.current;

		if (!video || !hasVideo) {
			return;
		}

		if (isActive) {
			const startPlayback = () => playVideoSafely(video);

			if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
				startPlayback();
				return;
			}

			video.addEventListener('loadeddata', startPlayback, { once: true });

			return () => {
				video.removeEventListener('loadeddata', startPlayback);
			};
		}

		video.pause();
		video.currentTime = 0;
	}, [hasVideo, isActive, resolvedVideoSrc]);

	const handleVideoError = () => {
		if (videoFallback && resolvedVideoSrc !== videoFallback) {
			setResolvedVideoSrc(videoFallback);
			setIsVideoReady(false);
			return;
		}

		setHasVideoError(true);
	};

	return (
		<GenderCardMedia data-hovered={showVideo ? 'true' : undefined}>
			<StudioCardImage
				src={imageSrc}
				alt={alt}
				priority={priority}
				objectPosition={objectPosition}
			/>
			{hasVideo ? (
				<GenderCardVideo
					ref={videoRef}
					key={resolvedVideoSrc}
					src={resolvedVideoSrc}
					muted
					loop
					playsInline
					preload="auto"
					poster={imageSrc}
					$visible={showVideo}
					onLoadedData={() => setIsVideoReady(true)}
					onCanPlay={() => {
						setIsVideoReady(true);
						if (isActive) {
							playVideoSafely(videoRef.current);
						}
					}}
					onError={handleVideoError}
				/>
			) : null}
		</GenderCardMedia>
	);
};

export default StudioCardHoverMedia;
